import { memo, useCallback, useState } from 'react';
import { Card, Container } from 'react-bootstrap';

import { ErrorAlert, LoadingSpinner, Pagination } from '@/components/common';
import { FacilitiesTable } from '@/components/facilities';
import { FacilityFilters } from '@/components/filters';
import { useFacilities, useFilterOptions } from '@/hooks';
import type {
  Facility,
  FacilityFilters as FacilityFiltersType,
  SortableField,
  SortConfig,
} from '@/types';

export const FacilitiesPage = memo(function FacilitiesPage() {
  const [filters, setFilters] = useState<FacilityFiltersType>({});
  const [sortConfig, setSortConfig] = useState<SortConfig | undefined>();
  const { options: filterOptions } = useFilterOptions();
  const {
    facilities,
    total,
    page,
    pageSize,
    loading,
    error,
    refetch,
    setPage,
    setFilters: updateFilters,
  } = useFacilities(filters);

  const handleFiltersChange = useCallback(
    (newFilters: FacilityFiltersType) => {
      setFilters(newFilters);
      updateFilters(newFilters);
    },
    [updateFilters]
  );

  const handleSort = useCallback(
    (field: SortableField) => {
      let newOrder: 'asc' | 'desc' = 'asc';

      if (sortConfig?.field === field) {
        // Toggle order if same field, or clear if already desc
        if (sortConfig.order === 'asc') {
          newOrder = 'desc';
        } else {
          // Clear sort
          setSortConfig(undefined);
          const { sort_by: _sortBy, sort_order: _sortOrder, ...rest } = filters;
          handleFiltersChange(rest);
          return;
        }
      }

      const newSortConfig: SortConfig = { field, order: newOrder };
      setSortConfig(newSortConfig);
      handleFiltersChange({
        ...filters,
        sort_by: field,
        sort_order: newOrder,
      });
    },
    [sortConfig, filters, handleFiltersChange]
  );

  const handleFacilityClick = useCallback((facility: Facility) => {
    const query = [
      facility.name,
      facility.address.street_1,
      facility.address.city,
      facility.address.province,
    ]
      .filter(Boolean)
      .join(' ');
    const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <Container fluid className="py-4">
      <h1 className="h3 mb-4">Childcare Facilities</h1>

      <Card className="mb-4">
        <Card.Body>
          <FacilityFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            filterOptions={filterOptions}
            loading={loading}
          />
        </Card.Body>
      </Card>

      {error && <ErrorAlert message={error} onRetry={refetch} />}

      {loading && !facilities.length ? (
        <LoadingSpinner message="Loading facilities..." />
      ) : (
        <>
          <Card>
            <Card.Body className="p-0">
              <FacilitiesTable
                facilities={facilities}
                sortConfig={sortConfig}
                onSort={handleSort}
                onFacilityClick={handleFacilityClick}
              />
            </Card.Body>
            <Card.Footer>
              <Pagination
                currentPage={page}
                totalItems={total}
                pageSize={pageSize}
                onPageChange={setPage}
                disabled={loading}
              />
            </Card.Footer>
          </Card>
        </>
      )}
    </Container>
  );
});
